/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Page,
  Card,
  BlockStack,
  Text,
  DropZone,
  LegacyStack,
  Thumbnail,
  Link,
  Banner,
  Icon,
  Button,
  Modal,
  IndexTable,
  LegacyCard,
  InlineStack,
} from "@shopify/polaris";
import { NoteIcon, InfoIcon, UploadIcon, DeleteIcon } from "@shopify/polaris-icons";
import { useDispatch } from "react-redux";
import { parse, ParseResult, ParseConfig } from "papaparse";
import { useImportOrdersMutation } from "../store/store/slices/OrderRTK";
import { showToast } from "../store/store/slices/AlertSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetFileUploadHistoryQuery } from "../store/store/slices/Payment";
import UploadHistoryTimeline from "../components/atoms/UploadHistoryTimeline";

type ParseError = {
  type: string;
  code: string;
  message: string;
  row?: number;
};

type CsvProcessingResult = {
  fileName: string;
  fileSize: number;
  lastModified: number;
  jsonData: Record<string, unknown>[];
  rawData: string;
  parseErrors: ParseError[];
  isValid: boolean;
};

type FileProcessingStatus = "idle" | "processing" | "success" | "error";

const MAX_FILE_SIZE_MB = 5;
const PREVIEW_ROW_LIMIT = 20;
const ACCEPTED_MIME_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "text/plain",
];

const REQUIRED_COLUMNS = [
  "tracking_number",
  "payment_status",
  "receivable_amount",
];

/** Required fields that must be non-empty in every row (invoice_cheque_no is optional) */
const REQUIRED_ROW_FIELDS = [
  "tracking_number",   // order id
  "payment_status",    // status
  "receivable_amount",
] as const;

const REQUIRED_FIELD_LABELS: Record<string, string> = {
  tracking_number: "Order ID (tracking_number)",
  payment_status: "Status (payment_status)",
  receivable_amount: "Receivable amount",
};

function getEmptyFieldNamesForRow(row: Record<string, unknown>): string[] {
  return REQUIRED_ROW_FIELDS.filter((key) => {
    const value = row[key];
    return value === undefined || value === null || String(value).trim() === "";
  });
}

function validateAllRowsHaveRequiredFields(
  jsonData: Record<string, unknown>[]
): { valid: boolean; message: string } {
  if (!jsonData.length) {
    return { valid: true, message: "" };
  }
  for (let i = 0; i < jsonData.length; i++) {
    const emptyFields = getEmptyFieldNamesForRow(jsonData[i]);
    if (emptyFields.length > 0) {
      const rowNum = i + 1;
      const fieldLabels = emptyFields
        .map((key) => REQUIRED_FIELD_LABELS[key] || key)
        .join(", ");
      return {
        valid: false,
        message: `Every field is required. Row ${rowNum}: ${fieldLabels} ${emptyFields.length === 1 ? "is" : "are"} empty.`,
      };
    }
  }
  return { valid: true, message: "" };
}

const SAMPLE_LINK =
  "https://cdn.shopify.com/s/files/1/0970/6097/8986/files/bookease_sample_file.csv?v=1767268534";

const CheckUploader = () => {
  const navigateTo = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [files, setFiles] = useState<File[]>([]);
  const [processingResults, setProcessingResults] = useState<CsvProcessingResult[]>([]);
  const [status, setStatus] = useState<FileProcessingStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [infoModalActive, setInfoModalActive] = useState(false);
  const params = new URLSearchParams(location.search);
  const page = params.get("page") || "1";
  const { data: fileUploadHistory, isLoading: historyLoading } = useGetFileUploadHistoryQuery({ page });

  const [
    importOrders,
    {
      isLoading: importOrdersLoading,
      isSuccess: importOrdersIsSuccess,
      data: importOrdersData,
    },
  ] = useImportOrdersMutation();

  const handleValidation = useCallback((file: File): string | null => {
    // Validate file type
    if (
      !ACCEPTED_MIME_TYPES.includes(file.type) &&
      !file.name.endsWith(".csv")
    ) {
      return "Only CSV files are accepted";
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`;
    }

    return null;
  }, []);


  const processCsvFile = useCallback(
    async (file: File): Promise<CsvProcessingResult> => {
      return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
          const csvData = event.target?.result as string;

          const parseConfig: ParseConfig = {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim(),
            transform: (value) => value.trim(),
            complete: (results: ParseResult<Record<string, unknown>>) => {
              resolve({
                fileName: file.name,
                fileSize: file.size,
                lastModified: file.lastModified,
                jsonData: results.data as Record<string, unknown>[],
                rawData: csvData,
                parseErrors: results.errors,
                isValid: results.errors.length === 0,
              });
            },
          };
          parse(csvData, parseConfig);
        };
        reader.onerror = () => {
          resolve({
            fileName: file.name,
            fileSize: file.size,
            lastModified: file.lastModified,
            jsonData: [],
            rawData: "",
            parseErrors: [
              {
                type: "reader",
                code: "file_read_error",
                message: "Failed to read file",
              },
            ],
            isValid: false,
          });
        };

        reader.readAsText(file);
      });
    },
    []
  );

  const validateCSVColumns = async (file: File, requiredColumns: string[]) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const text = e.target?.result as string;
        const firstLine = text.split("\n")[0].trim();
        const columns = firstLine.split(",").map((c) => c.trim());

        const missing = requiredColumns.filter((col) => !columns.includes(col));

        if (missing.length > 0) {
          reject(`Missing required columns: ${missing.join(", ")}`);
        } else {
          resolve(true);
        }
      };

      reader.onerror = () => reject("Failed to read file");
      reader.readAsText(file);
    });
  };

  const handleDropZoneDrop = useCallback(
    async (_dropFiles: File[], acceptedFiles: File[], rejectedFiles: File[]) => {
      setError(null);
      setStatus("processing");

      try {
        // Handle rejected files first
        if (rejectedFiles.length > 0) {
          const rejectionError = handleValidation(rejectedFiles[0]);
          setError(rejectionError || "The uploaded file is not valid");
          setStatus("idle");
          return;
        }

        // Validate accepted files for size (double check)
        const invalidFiles = acceptedFiles.filter(
          (file: File) => file.size > MAX_FILE_SIZE_MB * 1024 * 1024
        );

        if (invalidFiles.length > 0) {
          setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
          setStatus("idle");
          return;
        }

        try {
          await validateCSVColumns(acceptedFiles[0], REQUIRED_COLUMNS);
        } catch (msg) {
          const errorMsg = String(msg);
          setError(errorMsg);

          setStatus("idle");
          return;
        }

        // Process accepted files
        if (acceptedFiles.length > 0) {
          const results = await Promise.all(
            acceptedFiles.map((file: File) => processCsvFile(file))
          );

          // Validate every row has order id, status, receivable_amount (invoice_cheque_no optional)
          const validResults = results.filter((r) => r.isValid && r.jsonData.length > 0);
          if (validResults.length > 0) {
            const rowValidation = validateAllRowsHaveRequiredFields(validResults[0].jsonData);
            if (!rowValidation.valid) {
              setError(rowValidation.message);
              setStatus("idle");
              return;
            }
          }

          setFiles(acceptedFiles);
          setProcessingResults(results);
          setStatus("success");
        }
      } catch (err) {
        setStatus("error");
        setError("An unexpected error occurred while processing the file");
      }
    },
    [handleValidation, processCsvFile]
  );

  const handleDataUpload = useCallback(async () => {
    setStatus("processing");
    try {
      const validResults = processingResults.filter((result) => result.isValid);
      if (validResults.length === 0) {
        setError("No valid data to upload");
        setStatus("error");
        return;
      }
      const rowValidation = validateAllRowsHaveRequiredFields(validResults[0].jsonData);
      if (!rowValidation.valid) {
        setError(rowValidation.message);
        setStatus("idle");
        return;
      }
      await importOrders({
        ordersData: validResults[0].jsonData,
        fileName: validResults[0].fileName,
      });
    } catch (error) {
      setError("Failed to upload data");
      setStatus("error");
    }
  }, [processingResults, importOrders]);

  useEffect(() => {
    if (importOrdersData) {
      dispatch(
        showToast({
          message: importOrdersData?.msg,
          error: false,
          visible: true,
        })
      );
      // Reset after successful upload
      setFiles([]);
      setProcessingResults([]);
      setStatus("idle");
    }
  }, [importOrdersData, dispatch]);

  const errorBanner = useMemo(() => {
    if (!error) return null;
    const isColumnError = error.includes("Missing required columns:");
    return (
      <Banner
        tone="critical"
        onDismiss={() => {
          setError(null);
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{error}</span>
          {isColumnError && (
            <button
              onClick={() => setInfoModalActive(true)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon source={InfoIcon} />
            </button>
          )}
        </div>
      </Banner>
    );
  }, [error]);

  const successBanner = useMemo(() => {
    if (!importOrdersIsSuccess) return null;
    return (
      <Banner tone="success">
        Successfully uploaded {processingResults.length} file(s)
      </Banner>
    );
  }, [importOrdersIsSuccess, processingResults.length]);

  const fileUpload = !files.length && (
    <DropZone.FileUpload
      actionHint="Accepts .csv files up to 1MB"
      actionTitle="Upload CSV"
    />
  );

  const fileStats = useMemo(() => {
    if (processingResults.length === 0) return null;

    const totalRows = processingResults.reduce(
      (sum, result) => sum + result.jsonData.length,
      0
    );

    const errorFiles = processingResults.filter((r) => !r.isValid).length;

    return (
      <LegacyStack vertical spacing="tight">
        <Text as="p" variant="bodyMd">
          Processed {processingResults.length} file(s) with {totalRows} total
          rows
        </Text>
        {errorFiles > 0 && (
          <Text as="p" tone="critical">
            {errorFiles} file(s) had processing errors
          </Text>
        )}
      </LegacyStack>
    );
  }, [processingResults]);

  const uploadedFiles = files.length > 0 && (
    <div style={{ padding: "0" }}>
      <LegacyStack vertical>
        {files.map((file, index) => (
          <LegacyStack alignment="center" key={index}>
            <Thumbnail
              size="small"
              alt={file.name}
              source={
                ACCEPTED_MIME_TYPES.includes(file.type)
                  ? window.URL.createObjectURL(file)
                  : NoteIcon
              }
            />
            <div>
              {file.name}{" "}
              <Text variant="bodySm" as="p">
                {file.size} bytes
              </Text>
            </div>
          </LegacyStack>
        ))}
      </LegacyStack>
    </div>
  );

  // Prepare table data for display (first 20 rows only; full data still sent to backend on upload)
  const tableDisplay = useMemo(() => {
    if (
      status !== "success" ||
      processingResults.length === 0 ||
      !processingResults[0].isValid ||
      processingResults[0].jsonData.length === 0
    ) {
      return null;
    }

    const fullData = processingResults[0].jsonData;
    const tableData = fullData.slice(0, PREVIEW_ROW_LIMIT);
    const columnKeys = Object.keys(tableData[0]);
    
    if (columnKeys.length === 0) return null;

    const tableHeadings = columnKeys.map((key) => ({
      title: key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    }));

    if (tableHeadings.length === 0) return null;

    return { tableData, columnKeys, tableHeadings, totalRowCount: fullData.length };
  }, [status, processingResults]);

  return (
    <Page title="Cheque Uploader" backAction={{ onAction: () => navigateTo(-1) }}>
      <BlockStack gap={"600"}>
        <Card>
          <BlockStack gap="400">
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <Text variant="headingMd" as="h2">
                  Update Payment Status by CSV
                </Text>
                <button
                  onClick={() => setInfoModalActive(true)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Icon source={InfoIcon} />
                </button>
              </div>
              <Text tone="subdued" as="p">
                Upload a CSV file to update payment status for multiple orders
                at once.
              </Text>
            </div>  

            {errorBanner}
            {successBanner}

            <DropZone
              accept={ACCEPTED_MIME_TYPES.join(",")}
              type="file"
              allowMultiple={false}
              disabled={status === "processing" || importOrdersLoading}
              onDrop={handleDropZoneDrop}
            >
              {fileStats}
              {uploadedFiles}
              {fileUpload}
            </DropZone>

            <div style={{ marginTop: "10px" }} className="link_text_color">
              <Link target="_blank" url={SAMPLE_LINK}>
                Download sample CSV
              </Link>
            </div>

            {processingResults.length > 0 && (
              <InlineStack gap="400">
                <Button
                  variant={theme === "dark" ? "secondary" : "primary"}
                  icon={UploadIcon}
                  onClick={handleDataUpload}
                  disabled={status === "idle" || importOrdersLoading}
                  loading={importOrdersLoading || status === "processing"}
                >
                  Upload
                </Button>
                <Button
                  variant="secondary"
                  icon={DeleteIcon}
                  onClick={() =>{
                    setFiles([]);
                    setProcessingResults([]);
                    setStatus("idle");
                    setError(null);
                  }}
                >
                  Clear
                </Button>
              </InlineStack>
            )}
          </BlockStack>
        </Card>

        {/* Display CSV data in table format after validation */}
        {tableDisplay && (
          <Card>
            <BlockStack gap="400">
          
                <Text variant="headingMd" as="h2">
                  CSV File Data Preview
                </Text>
                <Text tone="subdued" as="p">
                  Review the data before uploading.{" "}
                  {tableDisplay.totalRowCount > PREVIEW_ROW_LIMIT
                    ? `Showing first ${PREVIEW_ROW_LIMIT} of ${tableDisplay.totalRowCount} rows.`
                    : `Total rows: ${tableDisplay.totalRowCount}`}
                </Text>
             
              <LegacyCard>
                <IndexTable
                  resourceName={{ singular: "row", plural: "rows" }}
                  itemCount={tableDisplay.tableData.length}
                  headings={tableDisplay.tableHeadings as any}
                  selectable={false}
                  hasZebraStriping
                >
                  {tableDisplay.tableData.map((row, rowIndex) => (
                    <IndexTable.Row
                      id={rowIndex.toString()}
                      key={rowIndex}
                      position={rowIndex}
                    >
                      {tableDisplay.columnKeys.map((key, cellIndex) => (
                        <IndexTable.Cell key={cellIndex}>
                          {row[key] !== null && row[key] !== undefined
                            ? String(row[key])
                            : ""}
                        </IndexTable.Cell>
                      ))}
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              </LegacyCard>
            </BlockStack>
          </Card>
        )}

        <Modal
          open={infoModalActive}
          onClose={() => setInfoModalActive(false)}
          title="CSV File Requirements"
          primaryAction={{
            content: "Got it",
            onAction: () => setInfoModalActive(false),
          }}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <div>
                <Text as="h3" variant="headingMd" fontWeight="semibold">
                  Required Columns:
                </Text>
                <LegacyStack vertical spacing="tight">
                  {REQUIRED_COLUMNS.map((col) => (
                    <Text key={col} as="p" variant="bodyMd">
                      • {col}
                    </Text>
                  ))}
                </LegacyStack>
                <Text as="p" variant="bodyMd" tone="subdued">
                  Optional: invoice_cheque_no
                </Text>
              </div>
              <div>
                <Text as="p" variant="bodyMd">
                  <strong>Note:</strong> If an order does not have receivable
                  amount, then COD amount will be its receivable amount and that
                  order will be marked as PAID.
                </Text>
              </div>
              <div>
                <Banner tone="warning">Only orders with UNPAID status will be marked as PAID. Already PAID orders will be ignored.</Banner>
              </div>
              <div>
                <Text as="p" variant="bodyMd">
                  <strong>File Size:</strong> CSV file should not exceed{" "}
                  {MAX_FILE_SIZE_MB}MB in size.
                </Text>
              </div>
            </BlockStack>
          </Modal.Section>
        </Modal>

        <UploadHistoryTimeline
          history={fileUploadHistory?.history || []}
          isLoading={historyLoading}
          pagination={fileUploadHistory?.pagination}
        />

      </BlockStack>
    </Page>
  );
};

export default CheckUploader;
